<template>
  <v-container class="details-tab">
    <div class="general-information">
      <h1 class="title ml-12">General information</h1>
      <hr class="underscore" id="general-information-underscore" />
      <v-row>
        <v-col lg="5" md="6">
          <p class="details-tab-label">Connected environment name</p>
        </v-col>
        <v-col lg="7" md="6">
          <div
            @click.self="enableEnvironmentNameEdit"
            v-if="details.AccessLevel || isUserOwner()"
          >
            <v-text-field
              class="environment-name"
              id="environment-name"
              ref="environmentName"
              maxlength="50"
              :hint="
                newEnvironmentName
                  ? `${newEnvironmentName.length}/50`
                  : `${environmentName.length}/50`
              "
              v-model="environmentName"
              @keyup.enter="updateName"
              :disabled="!editEnvironmentName"
              :loading="loading"
              :append-icon="
                !editEnvironmentName ? 'fal fa-edit' : 'fal fa-check'
              "
              @click:append="updateName"
              :append-outer-icon="editEnvironmentName ? 'fal fa-times' : null"
              @click:append-outer="disableEnvironmentNameEdit"
            ></v-text-field>
          </div>
          <p v-else>{{ details.ConnectedEnvironmentName }}</p>
        </v-col>
      </v-row>

      <v-row>
        <v-col lg="5" md="6">
          <p class="details-tab-label">Cloud provider</p>
        </v-col>
        <v-col lg="7" md="6">
          <div class="cloud-icons">
            <v-img
              v-if="details.Provider == 'az'"
              src="@/assets/azure.svg"
              class="cloud-provider-icon"
            ></v-img>
            <v-img
              v-else-if="details.Provider == 'aws'"
              src="@/assets/aws.svg"
              class="cloud-provider-icon"
            ></v-img>
            <v-img
              v-else
              src="@/assets/gcp.svg"
              class="cloud-provider-icon"
            ></v-img>
          </div>
        </v-col>
      </v-row>

      <v-row>
        <v-col lg="5" md="6">
          <p class="details-tab-label">Region</p>
        </v-col>
        <v-col lg="7" md="6">
          <p>{{ details.DisplayName }}</p>
        </v-col>
      </v-row>

      <v-row>
        <v-col lg="5" md="6">
          <p class="details-tab-label">Running on</p>
        </v-col>
        <v-col lg="7" md="6">
          <p>{{ details.TemplateType }}</p>
        </v-col>
      </v-row>
    </div>
    <div class="general-information">
      <h1 class="title env-title">Environments</h1>
      <hr class="underscore" id="general-information-underscore" />
      <div
        class="mt-4"
        v-for="env in details.environments"
        :key="env.EnvironmentId"
      >
        <h3 class="env-title mt-1 mb-1">
          <a
            :href="'http://' + env.FQDN"
            target="_blank"
            class="link"
            :class="{
              disabled: details.Status.toLowerCase() !== 'running',
              'hover-underline': details.Status.toLowerCase() === 'running',
            }"
          >
            {{ env.FQDN }}
            <v-icon
              v-if="details.Status.toLowerCase() === 'running'"
              class="title-icon"
              right
              small
              id="icon-style-pink"
              >fas fa-external-link-alt</v-icon
            >
          </a>
        </h3>
        <!-- <v-row>
          <v-col lg="5" md="6">
            <p class="details-tab-label">Name</p>
          </v-col>
          <v-col lg="7" md="6">
            <p>{{ env.Name }}</p>
          </v-col>
        </v-row> -->
        <v-row v-if="details.TemplateType.toUpperCase() === 'VM'">
          <v-col lg="5" md="6">
            <p class="details-tab-label">Size</p>
          </v-col>
          <v-col lg="7" md="6">
            <span id="instance-size"
              >{{ env.InstanceName.charAt(0).toUpperCase()
              }}{{ env.InstanceName.slice(1) }}:</span
            >
            <span
              >&nbsp; {{ env.vCPU }}&nbsp;vCPU,&nbsp; {{ env.RAM }}&nbsp; GB RAM
            </span>
          </v-col>
        </v-row>

        <!-- <v-row v-if="details.TemplateType.toUpperCase() === 'VM'">
          <v-col lg="5" md="6">
            <p class="details-tab-label">Port</p>
          </v-col>
          <v-col lg="7" md="6">
            <p>{{ env.Port }}</p>
          </v-col>
        </v-row> -->

        <v-row
          v-if="
            details.TemplateType.toUpperCase() === 'VM' &&
            details.OS === 'Windows'
          "
        >
          <v-col lg="5" md="6">
            <p class="details-tab-label">Login</p>
          </v-col>
          <v-col lg="7" md="6">
            <p>Administrator</p>
          </v-col>
        </v-row>
        <v-row
          v-if="
            details.TemplateType.toUpperCase() === 'VM' &&
            details.OS === 'Windows'
          "
        >
          <v-col lg="5" md="6">
            <p class="details-tab-label">Password</p>
          </v-col>
          <v-col lg="7" md="6">
            <v-row>
              <p class="ml-3" @click="copyPassword(env.EnvironmentId)">
                {{
                  rdpData.find((el) => el.id === env.EnvironmentId)
                    .hiddenRemoteEnvPassword
                    ? "***********"
                    : env.RdpPassword
                }}
              </p>
              <v-icon
                right
                small
                class="copy-icon ml-2 mb-2"
                @click="togglePassword(env.EnvironmentId)"
                :class="
                  rdpData.find((el) => el.id === env.EnvironmentId)
                    .hiddenRemoteEnvPassword
                    ? 'far fa-eye'
                    : 'fas fa-eye-slash'
                "
              ></v-icon>
            </v-row>
          </v-col>
        </v-row>
      </div>
    </div>
    <div class="environment-scheduler mr-10">
      <v-row>
        <v-col lg="4" md="5">
          <p class="details-tab-label">Connected environment scheduler</p>
        </v-col>
        <v-col lg="8" md="7" class="scheduler">
          <Scheduler
            :errors="false"
            :details="details"
            :schedulerLoading="schedulerLoading"
            @schedulerModificationConfirm="schedulerModificationConfirm"
          />
        </v-col>
      </v-row>
    </div>

    <div class="system-defined-data">
      <h1 class="title">System Defined Data</h1>
      <hr class="underscore" />
      <v-row>
        <v-col lg="5" md="6">
          <p class="details-tab-label">Created</p>
        </v-col>
        <v-col lg="7" md="6">
          <p>{{ convertToLocalTime(details.CreatedAt) }}</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col lg="5" md="6">
          <p class="details-tab-label">Updated</p>
        </v-col>
        <v-col lg="7" md="6">
          <p>{{ convertToLocalTime(details.UpdatedAt) }}</p>
        </v-col>
      </v-row>

      <!-- <v-row>
          <v-col lg="5" md="6">
            <p class="details-tab-label">Template Release Notes</p>
          </v-col>
          <v-col lg="7" md="6">
            <p>TODO</p>
          </v-col>
      </v-row>-->

      <v-row v-if="details.OwnerSub === sub">
        <v-col lg="5" md="6">
          <p class="details-tab-label">Access Level</p>
        </v-col>
        <v-col lg="7" md="6">
          <v-row>
            <v-switch
              class="ml-3 mt-0 pt-0 mr-3"
              :loading="accessSwitchLoading"
              hide-details
              @click="onAccessLevelSwitch"
              :input-value="accessLevelSwitch"
              :label="`Access Level - ${accessLevelSwitch ? 'admin' : 'user'}`"
            ></v-switch>
            <v-tooltip right max-width="20vw">
              <template v-slot:activator="{ on }">
                <v-icon v-on="on" small>fas fa-question-circle</v-icon>
              </template>
              <span>Access levels:</span>
              <ul>
                <li>
                  Admin - users with whom the environment has been shared have
                  the same level of access as the owner
                </li>
                <li>
                  User - users with whom the environment has been shared can
                  only start/stop the environment and change scheduler settings
                </li>
              </ul>
            </v-tooltip>
          </v-row>
        </v-col>
      </v-row>

      <v-row v-if="sharedTo.length > 0">
        <v-col lg="5" md="6">
          <p class="details-tab-label">Shared</p>
        </v-col>
        <v-col
          lg="7"
          md="6"
          v-if="details.AccessLevel || details.OwnerSub === sub"
        >
          <v-chip
            @click:close="onStopShare(user)"
            close
            v-for="(user, index) in sharedTo"
            :key="index"
            >{{ user.EmailAddress }}</v-chip
          >
        </v-col>
      </v-row>
    </div>
    <v-overlay :value="loadingDetails">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import Scheduler from "./DetailsScheduler";
import { mapActions } from "vuex";
import Template from "../../../Templates/Desktop/Template.vue";
export default {
  data() {
    return {
      editEnvironmentName: false,
      loading: false,
      newEnvironmentName: "",
      loadingDetails: false,
      sharedTo: [],
      touched: false,
      accessSwitchLoading: false,
      schedulerLoading: false,
      passwordRDP: "",
      hiddenRemoteEnvPassword: false,
      rdpData: [],
    };
  },
  props: ["details", "sub", "sharedEnvironmentWith"],
  components: { Scheduler, Template },
  computed: {
    accessLevelSwitch: {
      get() {
        return this.details.AccessLevel;
      },
    },
    environmentName: {
      get() {
        if (this.newEnvironmentName) {
          return this.newEnvironmentName;
        } else if (this.newEnvironmentName == "" && this.touched) {
          return "";
        } else {
          return this.details.ConnectedEnvironmentName;
        }

        // if (this.editEnvironmentName && this.newEnvironmentName == '') {
        //   return ''
        // }
      },
      set(newEnvironmentName) {
        this.touched = true;
        this.newEnvironmentName = newEnvironmentName;
      },
    },
  },
  methods: {
    ...mapActions([
      "stopSharingConnectedEnvironment",
      "updateConnectedEnvAccessLevel",
      "getConnectedEnvironmentDetails",
      "updateConnectedEnvironmentName",
      "getSharedEnvironmentWith",
      "modifyConnectedEnvironmentScheduler",
      "getUserSub",
      "showSnackbar",
    ]),
    togglePassword(id) {
      this.rdpData.map((env) => {
        if (env.id === id)
          env.hiddenRemoteEnvPassword = !env.hiddenRemoteEnvPassword;
      });
      // this.hiddenRemoteEnvPassword = !this.hiddenRemoteEnvPassword;
      // this.hiddenRemoteEnvPassword
      //   ? (this.passwordRDP = "********")
      //   : (this.passwordRDP = this.details.environments[0].RdpPassword);
    },
    copyPassword(id) {
      const { hiddenRemoteEnvPassword, password } = this.rdpData.find(
        (env) => env.id === id
      );
      if (!hiddenRemoteEnvPassword) {
        const textArea = document.createElement("textarea");
        textArea.value = password;
        document.body.appendChild(textArea);
        textArea.select();
        textArea.setSelectionRange(0, 9999999);
        document.execCommand("copy");
        textArea.remove();
        this.showSnackbar("Password has been copied!");
      }
    },
    async onStopShare(user) {
      const data = {
        connectedEnvId: this.details.ConnectedId,
        emails: [user.EmailAddress],
      };
      this.sharedTo = this.sharedTo.filter(
        (element) => element.EmailAddress !== user.EmailAddress
      );
      await this.stopSharingConnectedEnvironment(data);
      await this.getSharedEnvironmentWith(
        this.details.environments[0].EnvironmentId
      );
    },
    enableEnvironmentNameEdit() {
      this.editEnvironmentName = true;
      this.$nextTick(() => this.$refs.environmentName.focus());
    },
    async updateName() {
      if (
        this.newEnvironmentName === "" ||
        this.details.ConnectedEnvironmentName == this.newEnvironmentName
      ) {
        this.environmentName = this.details.ConnectedEnvironmentName;
        this.editEnvironmentName = false;
        return this.$refs.environmentName.blur();
      }

      this.loading = true;

      const data = {
        environmentId: this.details.ConnectedId,
        environmentName: this.newEnvironmentName,
      };

      this.loadingDetails = true;
      const { error, result } = await this.updateConnectedEnvironmentName(data);

      if (error) {
        this.environmentName = this.details.ConnectedEnvironmentName;
      }

      this.loading = false;

      this.loadingDetails = false;
      this.editEnvironmentName = false;
      this.$refs.environmentName.blur();
    },
    disableEnvironmentNameEdit() {
      if (this.newEnvironmentName == "") {
        this.environmentName = this.details.ConnectedEnvironmentName;
      }

      this.touched = false;
      this.$nextTick(() => this.$refs.environmentName.blur());
      this.editEnvironmentName = false;
      this.environmentName = this.details.ConnectedEnvironmentName;
    },
    async schedulerModificationConfirm(data, steps) {
      const { hourPostfixTo, hourPostfixFrom, ...params } = data;
      params.connectedId = this.details.ConnectedId;
      this.schedulerLoading = true;
      await this.modifyConnectedEnvironmentScheduler(params);
      await this.getConnectedEnvironmentDetails(this.details.ConnectedId);
      this.schedulerLoading = false;
    },
    async onAccessLevelSwitch() {
      const data = {
        connectedEnvId: this.details.ConnectedId,
        accessLevel: !this.accessLevelSwitch,
      };

      this.accessSwitchLoading = true;
      await this.updateConnectedEnvAccessLevel(data);
      await this.getConnectedEnvironmentDetails(this.details.ConnectedId);
      this.accessSwitchLoading = false;
    },
    isUserOwner() {
      if (this.sub == "") {
        this.getUserSub();
      }
      return this.sub == this.details.OwnerSub ? true : false;
    },
    convertToLocalTime(value) {
      const date = new Date(value);
      date.setUTCDate(date.getDate());
      date.setUTCFullYear(date.getFullYear());
      date.setUTCMonth(date.getMonth());
      date.setUTCHours(date.getHours());
      date.setUTCMinutes(date.getMinutes());
      date.setUTCSeconds(date.getSeconds());
      return (
        date.getFullYear() +
        "-" +
        ("0" + (date.getMonth() + 1)).slice(-2) +
        "-" +
        ("0" + date.getDate()).slice(-2) +
        " " +
        ("0" + date.getHours()).slice(-2) +
        ":" +
        ("0" + date.getMinutes()).slice(-2) +
        ":" +
        ("0" + date.getSeconds()).slice(-2)
      );
    },
  },
  beforeMount() {
    if (this.details.OS === "Windows") {
      this.rdpData = this.details.environments.map((env) => ({
        id: env.EnvironmentId,
        password: env.RdpPassword,
        hiddenRemoteEnvPassword: true,
      }));
    }
    this.sharedTo = this.sharedEnvironmentWith;
  },
  watch: {
    sharedEnvironmentWith() {
      this.sharedTo = this.sharedEnvironmentWith;
    },
  },
};
</script>

<style scoped>
.details-tab {
  font-family: "Montserrat";
}

.details-tab .title {
  margin-left: 40px;
  font-family: "Montserrat" !important;
  font-weight: bold;
  margin-top: 20px;
}

.details-tab {
  font-size: 0.85em;
}

.details-tab .col {
  padding-bottom: 0;
}

.details-tab-label {
  font-weight: bold;
  text-align: right;
}

.details-tab .environment-scheduler {
  background: #f9f9f9;
}

.underscore {
  background-color: #d0398b;
  border: 0.1vh solid #d0398b;
  border-radius: 0.1vh;
  width: 4vw;
  margin-top: 0.5vh;
  margin-left: 10.1vw;
}

#general-information-underscore {
  margin-left: 9.9vw;
}

.environment-name {
  margin-top: -6px;
  padding-top: 0;
}

.environment-name > .v-text-field__details {
  text-align: right;
}

.system-defined-data {
  margin-bottom: 8vh;
  padding-right: 12vw;
}

.general-information {
  padding-right: 12vw;
}

.cloud-icons {
  margin-top: -7px;
}
.cloud-provider-icon {
  width: 70px;
}

#instance-size {
  font-weight: bold;
}

.env-title {
  margin-left: 118px !important;
}

.disabled {
  pointer-events: none;
  cursor: default;
}

.link {
  color: black !important;
  text-decoration: none;
}

.hover-underline:hover {
  text-decoration: underline;
}
</style>
